/**
 * Centralized logging utility for the entire application
 * Automatically reduces logging in production builds
 * Consolidates all logging functionality from across the app
 */

// Log levels
export enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

// Log categories for better filtering
export enum LogCategory {
  API = 'API',
  DATABASE = 'DATABASE',
  STATE = 'STATE',
  UI = 'UI',
  AUTH = 'AUTH',
  MAPPING = 'MAPPING',
  NETWORK = 'NETWORK',
  USER_ACTION = 'USER_ACTION',
  PERFORMANCE = 'PERFORMANCE'
}

interface LogConfig {
  level: LogLevel;
  enableInProduction: boolean;
}

interface LogOptions {
  category?: LogCategory;
  timestamp?: boolean;
  data?: any;
  performance?: {
    startTime?: number;
    duration?: number;
  };
}

// Configuration - easily adjustable
const config: LogConfig = {
  level: import.meta.env.PROD ? LogLevel.WARN : LogLevel.DEBUG,
  enableInProduction: false
};

// Default options
const defaultOptions: Partial<LogOptions> = {
  timestamp: true,
  category: LogCategory.UI
};

// Check if logging should be enabled
const shouldLog = (level: LogLevel): boolean => {
  if (import.meta.env.PROD && !config.enableInProduction) {
    return level <= LogLevel.ERROR; // Only errors in production
  }
  return level <= config.level;
};

/**
 * Main logging function with timestamp and formatting
 */
export function logEvent(
  level: LogLevel, 
  message: string, 
  options: LogOptions = {}
) {
  if (!shouldLog(level)) return;
  
  const opts = { ...defaultOptions, ...options };
  const timestamp = opts.timestamp ? `[${new Date().toISOString()}]` : '';
  const category = opts.category ? `[${opts.category}]` : '';
  const levelStr = `[${LogLevel[level]}]`;
  
  // Different styling based on log level
  switch (level) {
    case LogLevel.INFO:
      console.info(`${timestamp} ${levelStr} ${category} ${message}`, opts.data || '');
      break;
    case LogLevel.WARN:
      console.warn(`${timestamp} ${levelStr} ${category} ${message}`, opts.data || '');
      break;
    case LogLevel.ERROR:
      console.error(`${timestamp} ${levelStr} ${category} ${message}`, opts.data || '');
      break;
    case LogLevel.DEBUG:
      console.debug(`${timestamp} ${levelStr} ${category} ${message}`, opts.data || '');
      break;
  }

  // Log performance data if provided
  if (opts.performance?.duration) {
    console.info(`${timestamp} ${levelStr} ${category} Performance: ${message} - ${opts.performance.duration}ms`);
  }
}

/**
 * Helper function to measure execution time
 */
export function measurePerformance<T>(
  fn: () => T, 
  message: string, 
  category: LogCategory = LogCategory.PERFORMANCE
): T {
  const startTime = performance.now();
  const result = fn();
  const duration = performance.now() - startTime;
  
  logEvent(LogLevel.INFO, message, {
    category,
    performance: { duration }
  });
  
  return result;
}

/**
 * Start measuring performance for async operations
 */
export function startMeasure(label: string): number {
  return performance.now();
}

/**
 * End performance measurement and log result
 */
export function endMeasure(startTime: number, message: string, category: LogCategory = LogCategory.PERFORMANCE): number {
  const duration = performance.now() - startTime;
  logEvent(LogLevel.INFO, message, {
    category,
    performance: { duration }
  });
  return duration;
}

// Convenience methods for different log levels
export const logInfo = (message: string, options: Omit<LogOptions, 'timestamp'> = {}) => 
  logEvent(LogLevel.INFO, message, { timestamp: true, ...options });

export const logWarn = (message: string, options: Omit<LogOptions, 'timestamp'> = {}) => 
  logEvent(LogLevel.WARN, message, { timestamp: true, ...options });

export const logError = (message: string, options: Omit<LogOptions, 'timestamp'> = {}) => 
  logEvent(LogLevel.ERROR, message, { timestamp: true, ...options });

export const logDebug = (message: string, options: Omit<LogOptions, 'timestamp'> = {}) => 
  logEvent(LogLevel.DEBUG, message, { timestamp: true, ...options });

// API call logging helpers
export const logApiCall = (endpoint: string, method: string, data?: any) => 
  logInfo(`API Call: ${method} ${endpoint}`, { category: LogCategory.API, data });

export const logApiResponse = (endpoint: string, status: number, data?: any) => 
  logInfo(`API Response: ${endpoint} - Status: ${status}`, { category: LogCategory.API, data });

// Database operation logging
export const logDbOperation = (operation: string, table: string, data?: any) => 
  logInfo(`DB ${operation}: ${table}`, { category: LogCategory.DATABASE, data });

// State change logging
export const logStateChange = (component: string, description: string, data?: any) => 
  logInfo(`State Change: ${component} - ${description}`, { category: LogCategory.STATE, data });

// UI event logging
export const logUiEvent = (component: string, event: string, data?: any, category: LogCategory = LogCategory.UI) => 
  logInfo(`UI Event: ${component} - ${event}`, { category, data });

// Simple logger object for backward compatibility
export const logger = {
  error: (message: string, ...args: any[]) => logError(message, { data: args.length > 0 ? args : undefined }),
  warn: (message: string, ...args: any[]) => logWarn(message, { data: args.length > 0 ? args : undefined }),
  info: (message: string, ...args: any[]) => logInfo(message, { data: args.length > 0 ? args : undefined }),
  debug: (message: string, ...args: any[]) => logDebug(message, { data: args.length > 0 ? args : undefined }),
  auth: (message: string, ...args: any[]) => logInfo(message, { category: LogCategory.AUTH, data: args.length > 0 ? args : undefined }),
  perf: (message: string, duration?: number, ...args: any[]) => {
    const perfOptions: LogOptions = { 
      category: LogCategory.PERFORMANCE,
      data: args.length > 0 ? args : undefined
    };
    if (duration) {
      perfOptions.performance = { duration };
    }
    logInfo(message, perfOptions);
  }
};

// Legacy console replacement (optional)
export const replaceConsole = () => {
  if (import.meta.env.PROD) {
    console.log = () => {}; // Disable console.log in production
    console.info = () => {}; // Disable console.info in production
    console.debug = () => {}; // Disable console.debug in production
    // Keep console.warn and console.error for important messages
  }
}; 